import { Link } from 'react-router-dom';
import { useSeo } from '../lib/seo';

const LAST_UPDATED = '2026-05-10';

export function Cookies() {
  useSeo({
    title: 'Cookies policy — Omega Trade and Finance',
    description: 'Omega Trade uses one strictly-necessary session cookie and no advertising, analytics or tracking cookies. No consent banner required under UK PECR.',
    canonicalPath: '/cookies',
  });
  return (
    <main>
      <section className="section legal-hero">
        <div className="container">
          <span className="eyebrow">Cookies policy</span>
          <h1>What cookies we set and why.</h1>
          <p className="lead" style={{ marginTop: 18, maxWidth: '64ch' }}>
            We use the minimum number of cookies required to operate the portal sign-in flow.
            We do not run advertising trackers, analytics cookies or third-party scripts on the
            public marketing site.
          </p>
        </div>
      </section>

      <section className="section">
        <div className="container">

          <Clause title="What is a cookie?">
            <p>
              A cookie is a small text file that a website stores in your browser to remember
              information between requests. The UK Privacy and Electronic Communications Regulations
              ("PECR") require us to tell you what cookies we use and to obtain your consent for
              non-essential ones.
            </p>
          </Clause>

          <Clause title="Cookies we set">
            <div className="data-table-wrap">
              <table className="data-table">
                <thead>
                  <tr><th>Name</th><th>Category</th><th>Purpose</th><th>Expiry</th></tr>
                </thead>
                <tbody>
                  <tr>
                    <td><Mono>omega_session</Mono></td>
                    <td><span className="chip">Strictly necessary</span></td>
                    <td>Keeps you signed in to the client or operator portal. HttpOnly + SameSite=Lax + Secure (production). Contains a signed JWT that references your session record in our database; no personal data is stored in the cookie value.</td>
                    <td>14 days, or until you sign out</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </Clause>

          <Clause title="Cookies we do not set">
            <ul className="checklist">
              <li><strong>No advertising cookies.</strong> We do not buy or sell online ads.</li>
              <li><strong>No third-party analytics cookies</strong> (Google Analytics, Hotjar, Segment, etc.). When we do measure traffic in future, we'll use a privacy-respecting first-party method and update this page.</li>
              <li><strong>No social-media tracking pixels.</strong></li>
              <li><strong>No marketing cookies.</strong></li>
            </ul>
            <p>
              Because the only cookie we set is strictly necessary to deliver a service you've
              explicitly requested (signing in), UK PECR Reg. 6(4) means we do not need a cookie
              consent banner for it.
            </p>
          </Clause>

          <Clause title="Managing cookies in your browser">
            <p>
              All modern browsers let you block cookies on a per-site basis. If you block our
              session cookie, the public marketing site (this site) will still work, but you will
              not be able to sign in to the portal.
            </p>
          </Clause>

          <Clause title="Updates">
            <p>
              We will update this page whenever we add or remove a cookie. Current version dated{' '}
              <Mono>{LAST_UPDATED}</Mono>. For details on the wider data we process, see our{' '}
              <Link to="/privacy" target="_blank" rel="noopener noreferrer">Privacy policy</Link>.
            </p>
          </Clause>

          <div className="legal-closer">
            <strong>Questions?</strong> Email <Mono>enquiry@omegatrade.co.uk</Mono>.
          </div>
        </div>
      </section>
    </main>
  );
}

function Clause({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="legal-block">
      <h2>{title}</h2>
      <div className="body">{children}</div>
    </div>
  );
}

function Mono({ children }: { children: React.ReactNode }) {
  return <span style={{ fontFamily: 'var(--mono)', fontSize: 13 }}>{children}</span>;
}
